<template>
  <div class="main">
    <div class="title">
      <h2>{{ title }}</h2>
      <AdminToolBar
        show-refresh
        @onRefreshAll="refreshAll"
      />
    </div>
    <div
      ref="content"
      class="content"
    >
      <Spin
        v-if="loading"
        fix
      >
        <Icon
          type="ios-loading"
          size="18"
          class="spin"
        />
        <div>Loading</div>
      </Spin>
      <template v-else>
        <div
          v-if="!list.length"
          class="no-data"
        >
          There isn't any registered Data to be Cached.
        </div>
        <Table
          v-else
          :width="contentWidth"
          :max-height="contentHeight"
          :row-class-name="tableRowClassName"
          :columns="columns"
          :data="list"
          class="table"
        >
          <template
            slot="lastUpdated"
            slot-scope="{ row }"
          >
            <span
              v-if="!row.lastUpdated"
              class="animated-dots"
            >
              Loading
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
            <span v-else>
              {{ row.lastUpdated }}
            </span>
          </template>
          <template
            slot="nextUpdate"
            slot-scope="{ row }"
          >
            <vue-countdown-timer
              :start-time="row.startTime"
              :interval="1000"
              :day-txt="null"
              hour-txt="hrs"
              minutes-txt="mins"
              seconds-txt="secs"
              @end_callback="resetCountDown(row.id)"
            />
          </template>
          <template
            slot="actions"
            slot-scope="{ row }"
          >
            <div>
              <i
                :class="refreshIcon(row.loading)"
                @click="!row.loading && refreshData(row.id)"
              />
            </div>
          </template>
        </Table>
      </template>
    </div>
  </div>
</template>

<script>
import AdminToolBar from './AdminToolBar'
import { mapState } from 'vuex'
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment-timezone'

export default {
  components: {
    AdminToolBar
  },

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      contentWidth: 0,
      contentHeight: 0,
      timeZone: 'America/Los_Angeles',
      loading: true,
      list: [
        {
          id: 1,
          description: 'Snowflake Metadata',
          vueX: {
            moduleName: 'snowflake',
            stateName: 'snowflakeData',
            actionName: 'fetchSnowflakeData',
            getterName: 'snowflakeLastUpdated'
          },
          api: {
            lastUpdated: {
              url: `${process.env.VUE_APP_API_URI}/admin-console/last-updated-date`,
              params: {
                data: 'snowflake'
              }
            },
            refresh: {
              params: {
                data: 'snowflake'
              }
            }
          },
          schedulerRules: {
            hour: 20,
            minute: 0
          },
          startTime: '',
          lastUpdated: null,
          loading: false
        },
        {
          id: 2,
          description: 'New Relics Dashboards',
          api: {
            lastUpdated: {
              url: `${process.env.VUE_APP_API_URI}/new-relic/last-updated`
            },
            refresh: {
              params: {
                data: 'new-relics'
              }
            }
          },
          schedulerRules: {},
          startTime: '',
          lastUpdated: null,
          loading: false
        }
      ],
      columns: [
        {
          title: 'Description',
          key: 'description'
        },
        {
          title: 'Last Update',
          slot: 'lastUpdated',
          align: 'left',
          width: '200'
        },
        {
          title: 'Next Update In',
          slot: 'nextUpdate',
          align: 'left',
          width: '160'
        },
        {
          title: 'Actions',
          slot: 'actions',
          align: 'center',
          width: '100'
        }
      ],
      schedulerTypes: [
        'Daily',
        'Weekdays',
        'Weekends'
      ],
      form: {
        description: '',
        apiUrl: '',
        schedulerType: null,
        time: null,
        day: null
      },
      formRules: {
        description: [
          { type: 'string', required: true, message: 'Description cannot be empty', trigger: ['blur', 'change'] }
        ],
        apiUrl: [
          { type: 'string', required: true, message: 'API URL cannot be empty', trigger: ['blur', 'change'] }
        ]
      }
    }
  },

  computed: {
    ...mapState('snowflake', {
      snowflakeData: state => state.snowflakeData,
      snowflakeTables: (state, getters) => getters.snowflakeTables,
      snowflakeLastUpdated: (state, getters) => getters.snowflakeLastUpdated
    })
  },

  mounted () {
    window.addEventListener('resize', this.getContentMeasures)
    this.getContentMeasures()
    this.fetchCachedData()
  },

  methods: {
    /**
     * Gets the height of the content according with the browser height.
     */
    getContentMeasures () {
      if (this.$refs.content) {
        this.contentWidth = this.$refs.content.offsetWidth
        this.contentHeight = this.$refs.content.offsetHeight
      }
    },

    /**
     * Sets the Class for a row on the table.
     * @param {Object} row
     * @param {Number} index
     */
    tableRowClassName (row) {
      return row.loading ? 'processing' : ''
    },

    refreshIcon (loading) {
      return `fas fa-sync-alt action-icon ${loading ? 'spin' : ''}`
    },

    /**
     * Fetches the content of each Cached Data
     */
    fetchCachedData () {
      this.loading = true

      this.list.forEach(async cachedData => {
        const { url, params } = cachedData.api.lastUpdated
        const lastUpdated = await this.getLastUpdated(url, params)
        this.$set(cachedData, 'startTime', this.setStartDateTime(cachedData))

        if (lastUpdated) {
          if (cachedData.vueX) {
            const { moduleName, stateName, actionName, getterName } = cachedData.vueX
            const getterPath = `${moduleName}/${getterName}`

            if (!this.$store.state[moduleName][stateName] || this.$store.getters[getterPath] !== lastUpdated) {
              this.$store.dispatch(`${moduleName}/${actionName}`)
            }
          }

          this.$set(cachedData, 'lastUpdated', lastUpdated)
        }
      })

      this.loading = false
    },

    /**
     * Sets the Countdown Start DateTime of a Cached Data.
     * @param {Object} cachedData
     * @return {String}
     */
    setStartDateTime (cachedData) {
      // If there is no scheduler rules, then don't set the countdown.
      if (_.isEmpty(cachedData.schedulerRules)) {
        return
      }

      const {
        hour = 0,
        minute = 0,
        second = 0
      } = cachedData.schedulerRules
      const currentDate = moment().tz(this.timeZone)
      const timeDiff = currentDate.diff({ hour, minute, second })

      if (timeDiff >= 0) {
        currentDate.set('date', currentDate.get('date') + 1)
      }

      currentDate.set('hour', +hour)
      currentDate.set('minute', +minute)
      currentDate.set('second', +second)
      return moment(currentDate).format('YYYY-MM-DD HH:mm:ss')
    },

    /**
     * Fetches the last updated datetime of a Cached Data in 'MMM-DD-YYYY hh:mm A z' format.
     * @param {String} apiUrl
     * @param {Object} params
     * @return {String}
     */
    async getLastUpdated (apiUrl, params) {
      try {
        const jsonData = (await axios.get(apiUrl, { params })).data
        return this.dateTimeToTimeZone(jsonData.lastUpdated)
      } catch (err) {
        const errorMessage = {
          title: 'Oops...',
          category: 'warning',
          message: `Unable to fetch last updated`,
          cancelButton: {
            label: 'OK'
          }
        }
        this.showModalMixin(errorMessage)
      }
    },

    /**
     * Transform a datetime value to a specific time zone
     * @param {String} dateTime
     * @param {String} timeZone
     * @param {String} format
     * @return {String}
     */
    dateTimeToTimeZone (dateTime, timeZone = this.timeZone, format = 'MMM-DD-YYYY hh:mm A z') {
      if (moment(dateTime, format, true).isValid()) {
        return dateTime
      }
      return moment(dateTime).tz(timeZone).format(format)
    },

    /**
     * Forces the refreshing of a Cached Data
     * @param {Number} id  Corresponding ID of the Cached Data
     */
    async refreshData (id) {
      const cachedData = this.list.find(data => data.id === id)
      this.$set(cachedData, 'loading', true)

      try {
        const result = await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/admin-console/cache-manager`,
          timeout: 1000 * 60 * 10, // wait for 10 minutes
          headers: {
            'Content-Type': 'application/json'
          },
          data: cachedData.api.refresh.params
        })

        // If the returned Last Updated isn't in 'Los Anteles' timezone and formated, update it.
        const lastUpdated = this.dateTimeToTimeZone(result.data.lastUpdated)
        this.$set(cachedData, 'lastUpdated', lastUpdated)
        this.$set(cachedData, 'loading', false)

        this.$Message.info({
          content: `${cachedData.description} refreshed successfully!`,
          duration: 5
        })
      } catch (error) {
        const content = `Failed to refresh ${cachedData.description}: ${error}`
        this.$Message.error({
          content,
          duration: 5
        })
        console.error(content)
        this.$set(cachedData, 'loading', false)
      }
    },

    /**
     * Force the refreshing of all the Cached Data.
     */
    refreshAll () {
      this.list.forEach(cachedData => {
        this.refreshData(cachedData.id)
      })
    },

    /**
     * Resets the countdown of a specific Cached Data.
     * @param {Number} id  Corresponding ID of the Cached Data
     */
    resetCountDown (id) {
      const cachedData = this.list.find(data => data.id === id)
      this.$set(cachedData, 'startTime', this.setStartDateTime(cachedData))
    }
  }
}
</script>

<style scoped>
  @keyframes blink {
    0% {
      opacity: .2;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: .2;
    }
  }

  .main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .spin,
  .table >>> .ivu-table-cell .spin {
    animation: ani-demo-spin 1s linear infinite;
  }

  .table >>> .ivu-table-body .ivu-table-row.processing td,
  .table >>> .ivu-table-body .ivu-table-row.processing td .ivu-table-cell .action-icon {
    background-color: var(--color-green-light);
    user-select: none;
    cursor: not-allowed;
  }

  .table >>> .ivu-table-body .ivu-table-row td .ivu-table-cell .animated-dots span {
    font-size: 20px;
    animation-name: blink;
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
  }

  .table >>> .ivu-table-body .ivu-table-row td .ivu-table-cell .animated-dots span:nth-child(2) {
    animation-delay: .2s;
  }

  .table >>> .ivu-table-body .ivu-table-row td .ivu-table-cell .animated-dots span:nth-child(3) {
    animation-delay: .4s;
  }

  .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .title .actions {
    width: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .table >>> .ivu-table-cell {
    padding: 0 8px;
  }

  .table >>> .ivu-table-cell .fas {
    font-size: 16px;
  }

  .table >>> .ivu-table-cell .action-icon {
    cursor: pointer;
  }

  .table >>> .ivu-table-cell .action-icon:not(:first-child) {
    margin-left: 8px;
  }

  .table >>> .ivu-table-cell .action-icon.hidden {
    display: none;
  }

  .no-data {
    font-size: 24px;
    color: var(--color-gray);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
